import { notify } from '@casinofe/react-toast';
import { requestLoginModal } from '@corefront/redux-aba';
import { isEmpty } from 'ramda';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DEFAULT_LOCIZE_NAMESPACE } from '../../../constants';
import {
  useMutationAddGameToFavourite,
  useMutationRemoveGameToFavourite,
  useQueryFavouriteGames,
} from '../../../hooks/API/useQueryFavouriteGames';
import { isLoggedInSelector } from '../../../redux/reducers/app/params';

export const useIsFavoriteGame = (gameId, productType) => {
  const isLoggedIn = useSelector(isLoggedInSelector);
  const { data, isLoading } = useQueryFavouriteGames(
    {
      enabled: isLoggedIn,
    },
    productType
  );

  return useMemo(() => {
    return isLoggedIn && !isLoading && !isEmpty(data)
      ? data?.find((game) => game.id === gameId)
      : false;
  }, [isLoggedIn, isLoading, data, gameId]);
};

export const useToggleFavouriteGame = (gameId, productType, queryOptions) => {
  const onSuccess = queryOptions?.onSuccess;
  const { t } = useTranslation(DEFAULT_LOCIZE_NAMESPACE);

  const dispatch = useDispatch();
  const isLoggedIn = useSelector(isLoggedInSelector);
  const isFavourite = useIsFavoriteGame(gameId, productType);

  const onError = useCallback(
    () =>
      notify.error({ title: t('Error'), message: t('Something went wrong') }),
    [t]
  );

  const { mutate: add } = useMutationAddGameToFavourite(
    gameId,
    {
      onError,
      onSuccess,
    },
    productType
  );
  const { mutate: remove } = useMutationRemoveGameToFavourite(
    gameId,
    {
      onError,
      onSuccess,
    },
    productType
  );

  if (!isLoggedIn) {
    return () => dispatch(requestLoginModal());
  }

  return isFavourite ? remove : add;
};
